<template>
    <div class="file-chip">
        <e-taglist attached>
            <e-tag
                v-if="showLeftTag"
                :tooltip="fullFileName"
                tabindex="0"
                :closable="closable"
                attached
                size="is-medium"
                class="tw:cursor-pointer"
                aria-close-label="Remove file"
                @close="$emit('delete', file)"
                @focus="showTooltip"
                @blur="hideTooltip"
                @click="showTooltip"
            >
                <span v-if="customDownload && title" class="tw:truncate">{{ title }}</span>
                <span v-else-if="fileName" class="tw:truncate">{{ fileName }}</span>
                <span v-else>File</span>
                <template v-if="fileSize">
                    &nbsp;
                    <small>{{ fileSize }}</small>
                </template>
            </e-tag>
            <e-tag v-if="showViewControl" size="is-medium" attached>
                <e-tooltip :label="viewTooltip" :active="!canView" multilined append-to-body>
                    <button
                        type="button"
                        class="tag has-text-weight-bold is-clickable tw:bg-transparent tw:bg-[var(--p-tag-primary-background)]! file-chip-view-btn"
                        :class="{ 'is-disabled': !canView }"
                        :aria-label="viewAriaLabel"
                        :disabled="!canView"
                        data-test="file-chip-view"
                        @click="onView"
                    >
                        <e-icon icon="eye" size="is-small" type="is-primary" />
                    </button>
                </e-tooltip>
            </e-tag>
            <e-tag v-if="downloadHref || customDownload || title" size="is-medium" attached>
                <a
                    :href="downloadHref || '#'"
                    :download="customDownload ? undefined : fullFileName || 'File'"
                    class="tag has-text-weight-bold is-clickable tw:bg-transparent tw:bg-[var(--p-tag-primary-background)]!"
                    data-test="file-chip-download"
                    @click="download"
                >
                    <e-icon v-if="downloadStatus === 'downloaded'" icon="check-circle" size="is-small" type="is-primary" />
                    <e-icon v-if="downloadStatus === 'none'" icon="download-alt" size="is-small" type="is-primary" />
                    <spinner v-if="downloadStatus === 'downloading'" size="20" />
                </a>
            </e-tag>
        </e-taglist>
    </div>
</template>

<script>
import prettyBytes from 'pretty-bytes';

import Spinner from '@/components/chat/system/Spinner.vue';
import DownloadStatuses from '@/mixins/DownloadStatuses.mixin.js';
import { FILE_VIEWER_UNSUPPORTED_TOOLTIP, getFileViewerCapability } from '@/utilities/fileViewerCapability.js';
import { resolveMediaDownloadHref } from '@/utilities/mediaDownloadHref.js';

export default {
    name: 'FileChip',

    components: { Spinner },

    mixins: [DownloadStatuses],

    props: {
        file: {
            type: [Object, File],
            required: true,
        },

        canClose: {
            type: Boolean,
            default: false,
        },

        customDownload: {
            type: Boolean,
            default: false,
        },

        /**
         * Opt-in File Viewer control. Hidden by default so chips without a
         * `@view` listener (imports, media forms) do not show a dead eye button.
         */
        enableView: {
            type: Boolean,
            default: false,
        },

        downloadStatus: {
            type: String,
            required: false,
            default: 'none',
        },

        showLeftTag: {
            type: Boolean,
            required: false,
            default: true,
        },
    },

    emits: ['download', 'delete', 'view'],

    computed: {
        closable() {
            return this.canClose && this.$options.emits.includes('delete');
        },

        fullFileName() {
            if (this.customDownload) {
                if (!this.$store.state.SlideDecks[`${this.file.model_id}`]) {
                    return null;
                }

                const raw = this.$store.getters[`SlideDecks/${this.file.model_id}/get`]('title');

                return raw ? `${raw}.pdf` : null;
            }

            if (!this.file) {
                return null;
            }

            return this.file.name || this.file.fileName || this.file.originalName || this.file.title || null;
        },

        title() {
            return this.customDownload ? this.truncateFileName(this.fullFileName) : null;
        },

        fileName() {
            return this.customDownload ? null : this.truncateFileName(this.fullFileName);
        },

        fileSize() {
            if (!this.file || !this.file.size) {
                return null;
            }
            try {
                return this.prettyBytes(this.file.size);
            } catch (error) {
                return null;
            }
        },

        capability() {
            return getFileViewerCapability(this.file);
        },

        showViewControl() {
            return this.enableView && !this.customDownload;
        },

        canView() {
            return this.capability.supported;
        },

        viewAriaLabel() {
            return `View ${this.fullFileName || 'File'}`;
        },

        viewTooltip() {
            return this.canView ? '' : FILE_VIEWER_UNSUPPORTED_TOOLTIP;
        },

        downloadHref() {
            if (this.customDownload) {
                return this.file.public_url || null;
            }

            return resolveMediaDownloadHref(this.file);
        },
    },

    methods: {
        prettyBytes,

        // PrimeVue tooltip is hover-only; synthesize hover on focus/click for tablet tap.
        showTooltip(event) {
            const el = event.currentTarget;
            requestAnimationFrame(() => el?.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true })));
        },

        hideTooltip(event) {
            event.currentTarget?.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
        },

        truncateFileName(name, maxLength = 35) {
            if (!name || name.length <= maxLength) {
                return name;
            }

            const ext = name.lastIndexOf('.') !== -1 ? name.slice(name.lastIndexOf('.')) : '';

            return `${name.slice(0, maxLength - 3 - ext.length)}...${ext}`;
        },

        download($event) {
            if (this.customDownload) {
                this.$emit('download');
                $event.preventDefault();
            }
        },

        onView() {
            if (!this.canView) {
                return;
            }

            this.$emit('view', this.file);
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

// TODO: fix spacing jump from one to multiple
.file-chip {
    margin-bottom: $tag-gap !important;
    overflow: hidden;

    .e-tags {
        max-width: 100%;
        flex-wrap: nowrap;
    }

    .p-tag:first-child {
        min-width: 0;
        overflow: hidden;

        > span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

.file-chip-view-btn {
    border: 0;
    padding: 0;
    line-height: 1;

    &.is-disabled,
    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
}
</style>
